// Shared pieces for the Profit section (Starchive-Profit-Engine-Plan; mockup "Starchive Profit Engine", 30 Sep 2026).
// The engine's raw sums come from reports-read (supabase/functions/_shared/profit.ts); the rates are worked out here with the
// handling cost Shane set, so what the page shows behind "the arithmetic" is exactly what it used.
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMeta } from '../../ReportsApp'
import { m, m0 } from '../money'
import '../../profit.css'

export interface PSettings {
  start_date: string
  rack_capacity: number | null; storage_capacity: number | null
  handling_minutes: number | null; hourly_cost: number | null; consumables: number | null
  buying_budget: number | null; budget_month: string | null
  cost_of_cash_pct: number
  rack_limit_pct: number; cash_limit_pct: number
  price_bands: number[]
  consign_full_days: number; consign_ask_pct: number; consign_return_days: number
  markdown_max_step_pct: number; markdown_min_days: number; markdown_start_days: number
  evidence_rank: number; evidence_show: number; evidence_use: number; evidence_group: number; evidence_store: number
  market_weight_pct: number
  masih_in_profit: boolean
  updated_by: string | null; updated_name: string | null; updated_at: string | null
}
export interface Handling { set: boolean; per_piece: number }
export interface Stats {
  n: number; gp: number; gp_owned: number; n_owned: number; sales: number; listed: number; first: number
  sd: number; dd: number; pieces: number; floor: number; med: number | null; p75: number | null
  st30: number | null; st60: number | null; st90: number | null
}

export { m, m0 }
export const pc = (v: number | null | undefined) => v == null ? '—' : Math.round(v * 100) + '%'
export const int = (v: number) => Math.round(v).toLocaleString('en-US')
export const lower = (t: string) => t.toLowerCase().replace(/&/g, 'and')
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const dm = (d: string | null | undefined) => d ? `${Number(d.slice(8, 10))} ${SHORT[Number(d.slice(5, 7)) - 1]}` : ''
export const monthOf = (ym: string) => { const [y, mm] = ym.split('-').map(Number); return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][mm - 1]} ${y}` }
export const nice = (p: number) => { const s = p < 200 ? 5 : p < 1000 ? 10 : p < 5000 ? 50 : 100; return Math.round(p / s) * s }
export const priceStep = (p: number) => p < 200 ? 5 : p < 1000 ? 10 : p < 5000 ? 50 : 100
export const weeks = (d: number) => d < 13.5 ? `about ${Math.max(1, Math.round(d))} day${Math.round(d) === 1 ? '' : 's'}` : `about ${Math.round(d / 7)} weeks`

/** Rates for one group from its raw sums: profit after handling, profit a day per rack spot, per $1,000 tied up, per piece. */
export function rates(g: Stats, h: number) {
  const np = g.gp - h * g.n
  return { np, psd: g.sd ? np / g.sd : 0, cash: g.dd ? (g.gp_owned / g.dd) * 1000 : null as number | null, ppp: g.n ? np / g.n : 0 }
}

// ---- icons ----------------------------------------------------------------------------------------------------------------------
const TYPE_ICON: Record<string, string> = {
  'Tees': 'M8 4L3 7l2 4 3-1v10h8V10l3 1 2-4-5-3a4 3 0 0 1-8 0z',
  'Long sleeves': 'M8 4L5 5.5 2.5 18H5l3-8v10h8V10l3 8h2.5L19 5.5 16 4a4 3 0 0 1-8 0z',
  'Shirts': 'M8 4l4 3 4-3 4 3-2 4-2-1v10H8V10l-2 1-2-4zM12 7v13M12 11h.01M12 15h.01',
  'Tops': 'M8 4L3 7l2 4 3-1v10h8V10l3 1 2-4-5-3a4 3 0 0 1-8 0z',
  'Hoodies & Sweaters': 'M8 4h8l4 4-2 3-2-1v10H8V10l-2 1-2-3 4-4zM10 4a2 2.5 0 0 0 4 0M10 16h4',
  'Outerwear': 'M8 3l4 3 4-3 5 4v13h-5v-9M8 3L3 7v13h5v-9M12 6v14',
  'Pants & Denim': 'M7 3h10l1 18h-4l-2-11-2 11H6zM7 6h10',
  'Shorts': 'M6 5h12l1 11h-5l-2-5-2 5H5zM6 8h12',
  'Accessories': 'M4 17h16M6 17c0-6 2.5-10 6-10s6 4 6 10M12 7V5',
  'Jewelry': 'M12 9a6 6 0 1 0 0 12 6 6 0 1 0 0-12zM9 3h6l-3 6z',
  'Eyewear': 'M3 12a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0-7 0M14 12a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0-7 0M10 12h4M3 12L2 9M21 12l1-3',
  'Bags': 'M5 8h14l-1 12H6zM9 8V6a3 3 0 0 1 6 0v2',
  'Shoes': 'M3 17h18v-2l-7-2-2-4H8l-1 2H3zM3 17v2h18v-2',
  'Objects & Art': 'M4 4h16v16H4zM4 15l5-5 4 4 3-3 4 4',
  'Other': 'M3 12V3h9l9 9-9 9zM7.5 7.5h.01',
}
export function Svg({ d, size = 16, w = 2 }: { d: string; size?: number; w?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
}
export const ICON = {
  up: 'M12 19V5M6 11l6-6 6 6', tag: 'M3 12V3h9l9 9-9 9zM7.5 7.5h.01', msg: 'M4 5h16v11H9l-5 4z', ret: 'M9 14l-5-5 5-5M4 9h11a5 5 0 0 1 0 10h-3',
  star: 'M12 3l2.6 5.6 6 .7-4.5 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6L3.4 9.3l6-.7z', sliders: 'M4 7h9M17 7h3M4 12h3M11 12h9M4 17h11M19 17h1M15 5v4M9 10v4M17 15v4',
  ok: 'M5 12l4 4 10-10', print: 'M6 9V3h12v6M6 18H4v-7h16v7h-2M6 14h12v7H6z', chev: 'M9 6l6 6-6 6',
}
export function TypeIcon({ type, size = 18 }: { type: string; size?: number }) { return <Svg d={TYPE_ICON[type] ?? TYPE_ICON.Other} size={size} w={1.7} /> }
/** A piece's photo (Shopify's CDN, asked small) or its type's outline. */
export function Pic({ src, type, big }: { src?: string | null; type: string; big?: boolean }) {
  const [bad, setBad] = useState(false)
  const url = src && !bad ? src + (src.includes('?') ? '&' : '?') + `width=${big ? 240 : 120}` : null
  return <span className={'pf-pic' + (big ? ' pf-pic--big' : '')}>{url ? <img src={url} alt="" loading="lazy" onError={() => setBad(true)} /> : <TypeIcon type={type} size={big ? 40 : 18} />}</span>
}

export function Chip({ tone = 'plain', children, icon }: { tone?: 'plain' | 'good' | 'warn' | 'bad' | 'grey' | 'dark'; children: ReactNode; icon?: string }) {
  return <span className={'pf-chip' + (tone !== 'plain' ? ' pf-chip--' + tone : '')}>{icon && <Svg d={icon} size={11} w={3} />}{children}</span>
}

export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: [T, ReactNode][]; onChange: (v: T) => void; label: string }) {
  return <div className="rp-vseg" role="group" aria-label={label}>{options.map(([k, l]) => <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>{l}</button>)}</div>
}

/** The four-digit owner ID for Profit actions, typed once and kept for this tab. */
const OWNER_KEY = 'starchive.reports.profit.owner'
export function useOwner(): [string, (v: string) => void, string | null] {
  const { meta } = useMeta()
  const [id, setId] = useState(() => { try { return sessionStorage.getItem(OWNER_KEY) ?? '' } catch { return '' } })
  const name = id.length === 4 ? meta?.owners.find((o) => o.employee_id === id)?.name ?? null : null
  useEffect(() => { try { if (name) sessionStorage.setItem(OWNER_KEY, id) } catch { /* ignore */ } }, [id, name])
  return [id, (v: string) => setId(v.replace(/[^0-9]/g, '').slice(0, 4)), name]
}
export function OwnerField({ id, setId, name, hint }: { id: string; setId: (v: string) => void; name: string | null; hint?: string }) {
  return (
    <label className="rp-field">
      <span className="rp-field__label">Owner ID</span>
      <input className="rp-input" inputMode="numeric" autoComplete="off" value={id} onChange={(e) => setId(e.target.value)} placeholder="••••" style={{ letterSpacing: '.2em', width: 120 }} />
      <span className="rp-field__hint">{id.length === 4 ? (name ? `Signed by ${name}` : 'Not an owner ID') : hint ?? 'Four digits'}</span>
    </label>
  )
}

/** A small message in the corner that goes away by itself. */
export function useToast(): [ReactNode, (msg: string, small?: string, bad?: boolean) => void] {
  const [t, setT] = useState<{ msg: string; small?: string; bad?: boolean; k: number } | null>(null)
  useEffect(() => { if (!t) return; const x = setTimeout(() => setT(null), 3800); return () => clearTimeout(x) }, [t])
  const node = t ? <div key={t.k} className={'pf-toast' + (t.bad ? ' pf-toast--bad' : '')} role="status">{!t.bad && <span style={{ color: '#30d158' }}><Svg d={ICON.ok} size={16} w={3} /></span>}<div>{t.msg}{t.small && <small>{t.small}</small>}</div></div> : null
  return [node, (msg, small, bad) => setT({ msg, small, bad, k: Date.now() })]
}

/** "Not set" chips for the numbers a screen depends on, each a link to Settings. */
export function SetChips({ s, h, show, month }: { s: PSettings; h: Handling; show: ('handling' | 'rack' | 'budget')[]; month: string }) {
  return (
    <>
      {show.includes('handling') && <Link to="/profit/settings" className={'pf-setchip' + (h.set ? '' : ' pf-setchip--unset')}><span>Handling</span><b>{h.set ? m(h.per_piece) : 'not set'}</b>{h.set && <span>a piece</span>}</Link>}
      {show.includes('rack') && <Link to="/profit/settings" className={'pf-setchip' + (s.rack_capacity ? '' : ' pf-setchip--unset')}><span>Rack</span><b>{s.rack_capacity ? int(s.rack_capacity) : 'not set'}</b>{s.rack_capacity ? <span>spots</span> : null}</Link>}
      {show.includes('budget') && <Link to="/profit/settings" className={'pf-setchip' + (s.buying_budget != null && s.budget_month === month ? '' : ' pf-setchip--unset')}><span>Budget</span><b>{s.buying_budget != null && s.budget_month === month ? m0(s.buying_budget) : 'not set'}</b></Link>}
    </>
  )
}
